import { Form } from 'react-bootstrap';
import type { TFunction } from 'i18next';
import { KEISUKE_CHART_FONT_FAMILY_PRESETS } from './chartUtils';
import {
  applyUnifiedKeisukeFont,
  type KeisukeChartSettingsWithUnifiedFont,
  type KeisukeUnifiedFontPatch,
  type KeisukeUnifiedFontStyle,
} from './keisukeUnifiedChartFontApply';

type SetState<T> = (value: T | ((prev: T) => T)) => void;

type Props<T extends KeisukeChartSettingsWithUnifiedFont> = {
  t: TFunction;
  chartSettings: T;
  setChartSettings: SetState<T>;
  /** 指定時、フォント項目をこのクラスでラップ（ダッシュボード等で横並び用） */
  fieldsRowClassName?: string;
  /** 見出し h6 のクラス（ダッシュボードのセクション見出し用） */
  headingClassName?: string;
  /** 見出し左の Font Awesome クラス（例: fas fa-font） */
  headingIconClass?: string;
  /** アイコン枠のクラス（親から CSS Modules を渡す） */
  headingIconWrapperClassName?: string;
  /** フィールド id / name の接頭辞（未指定時は keisuke-chart-font-unified） */
  fieldIdPrefix?: string;
};

export default function KeisukeUnifiedChartFontControls<T extends KeisukeChartSettingsWithUnifiedFont>({
  t,
  chartSettings,
  setChartSettings,
  fieldsRowClassName,
  headingClassName,
  headingIconClass,
  headingIconWrapperClassName,
  fieldIdPrefix,
}: Props<T>) {
  const fid = (suffix: string) => `${fieldIdPrefix ?? 'keisuke-chart-font-unified'}-${suffix}`;

  const patch = (p: KeisukeUnifiedFontPatch) =>
    setChartSettings((prev) => applyUnifiedKeisukeFont(prev, p));

  const { axisLabel, tickLabel } = chartSettings.textStyles;
  const leg = chartSettings.legend.textStyle;
  const family = axisLabel.fontFamily;
  const fontWeight = tickLabel.fontWeight;
  const fontStyle = (leg.fontStyle ?? tickLabel.fontStyle ?? 'normal') as KeisukeUnifiedFontStyle;
  const fontSize = axisLabel.fontSize;
  const color = tickLabel.color;
  const groupClass = fieldsRowClassName ? undefined : 'mb-2';

  const fontFields = (
    <>
      <Form.Group className={groupClass} controlId={fid('family')}>
          <Form.Label className="small">
            {t('keisuke.chart.unifiedFontFamily')}
          </Form.Label>
          <Form.Select
            size="sm"
            name={fid('family')}
            aria-label={t('keisuke.chart.unifiedFontFamily')}
            value={family}
            onChange={(e) => patch({ fontFamily: e.target.value })}
          >
            {!KEISUKE_CHART_FONT_FAMILY_PRESETS.some((p) => p.value === family) &&
              family.trim() !== '' && (
                <option value={family}>
                  {t('keisuke.dashboard.chart.fontFamilyCustomSaved', {
                    value: family,
                  })}
                </option>
              )}
            {KEISUKE_CHART_FONT_FAMILY_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className={groupClass} controlId={fid('weight')}>
          <Form.Label className="small">
            {t('keisuke.chart.unifiedFontWeight')}
          </Form.Label>
          <Form.Select
            size="sm"
            name={fid('weight')}
            value={fontWeight}
            onChange={(e) => patch({ fontWeight: e.target.value })}
          >
            <option value="normal">{t('keisuke.dashboard.chart.fontWeightNormal')}</option>
            <option value="500">{t('keisuke.dashboard.chart.fontWeightNumeric500')}</option>
            <option value="600">{t('keisuke.dashboard.chart.fontWeightNumeric600')}</option>
            <option value="700">{t('keisuke.dashboard.chart.fontWeightNumeric700')}</option>
            <option value="bold">{t('keisuke.dashboard.chart.fontWeightBold')}</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className={groupClass} controlId={fid('style')}>
          <Form.Label className="small">
            {t('keisuke.chart.unifiedFontStyle')}
          </Form.Label>
          <Form.Select
            size="sm"
            name={fid('style')}
            value={fontStyle}
            onChange={(e) => patch({ fontStyle: e.target.value as KeisukeUnifiedFontStyle })}
          >
            <option value="normal">{t('keisuke.dashboard.chart.fontStyleNormal')}</option>
            <option value="italic">{t('keisuke.dashboard.chart.fontStyleItalic')}</option>
            <option value="bold">{t('keisuke.dashboard.chart.fontStyleBold')}</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className={groupClass} controlId={fid('size')}>
          <Form.Label className="small">
            {t('keisuke.chart.unifiedFontSize')}
          </Form.Label>
          <Form.Control
            type="number"
            size="sm"
            name={fid('size')}
            min={8}
            max={28}
            step={1}
            value={fontSize}
            onChange={(e) => patch({ fontSize: parseInt(e.target.value, 10) || 12 })}
          />
        </Form.Group>
        <Form.Group className={groupClass} controlId={fid('color')}>
          <Form.Label className="small">
            {t('keisuke.chart.unifiedFontColor')}
          </Form.Label>
          <Form.Control
            type="color"
            size="sm"
            name={fid('color')}
            value={color}
            onChange={(e) => patch({ color: e.target.value })}
          />
        </Form.Group>
    </>
  );

  const headingCls = headingClassName ?? 'mb-2 small';

  return (
    <>
      <h6 className={headingCls}>
        {headingIconClass && headingIconWrapperClassName ? (
          <span className={headingIconWrapperClassName} aria-hidden>
            <i className={headingIconClass} />
          </span>
        ) : null}
        {t('keisuke.chart.unifiedFontHeading')}
      </h6>
      <p className="small text-muted mb-2">
        {t('keisuke.chart.unifiedFontHint')}
      </p>
      {fieldsRowClassName ? <div className={fieldsRowClassName}>{fontFields}</div> : fontFields}
    </>
  );
}
